import React from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import {
  Button,
  Divider,
  Grid,
  Stack
} from '@mui/material';
import {
  CardNumberElement,
  CardExpiryElement,
  CardCvcElement,
  useStripe,
  useElements
} from '@stripe/react-stripe-js';
import {
  CreateSubscriptionInput,
  SubscriptionSecrets
} from 'generated/graphql';
import {
  fields,
  PaymentCountrySelectField,
  USStateSelectField
} from 'modules';
import { routeDirectory } from 'modules';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';

import * as dataService from '../dataService';



const ControlledTextField = fields.genTextFieldControllerComponent<dataService.IFormData>();
const USStateSelectFieldController = fields.genEnumSelectFieldControllerComponent<dataService.IFormData, string>(USStateSelectField({
  label: 'State'
}));
const ControlledStripeElementField = fields.genStripeElementField<dataService.IFormData>();
const PaymentCountrySelectFieldController = fields.genEnumSelectFieldControllerComponent<dataService.IFormData, string>(PaymentCountrySelectField);



interface ICreditCardProps {
  onCreate: (args: CreateSubscriptionInput) => Promise<SubscriptionSecrets>;
  onChangeLoading: (loading: boolean) => void;
}

export const CreditCardForm: React.FC<ICreditCardProps> = props => {
  const navigate = useNavigate();
  const form = useForm<dataService.IFormData>({
    resolver: yupResolver(dataService.__formSchema),
    shouldFocusError: true
  });
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = React.useState(false);

  React.useEffect(() => props.onChangeLoading(loading), [loading, props]);



  async function processSubmit(data: dataService.IFormData) {
    if (!stripe || !elements) {
      return;
    }

    const cardNumberElement = elements.getElement(CardNumberElement);
    const cardExpiryElement = elements.getElement(CardExpiryElement);
    const cardCvcElement = elements.getElement(CardCvcElement);

    setLoading(true);
    if (cardNumberElement && cardExpiryElement && cardCvcElement) {
      const { paymentIntentClientSecret, setupIntentClientSecret } = await props.onCreate({
        country: data.country,
        usState: data.usState,
        city: data.city,
        address: data.address,
        zipCode: data.zipCode,
        discountCode: data.discountCode
      });

      if (paymentIntentClientSecret) {
        const { error } = await stripe.confirmCardPayment(paymentIntentClientSecret, {
          payment_method: {
            card: cardNumberElement,
            billing_details: {
              address: {
                state: data.usState,
                city: data.city,
                line1: data.address,
                postal_code: data.zipCode
              }
            }
          }
        });

        if (error) {
          alert(error.message);
        } else {
          navigate(routeDirectory.authentication.onboardingComplete.path());
        }
      } else if (setupIntentClientSecret) {
        const { error } = await stripe.confirmCardSetup(setupIntentClientSecret,
          { payment_method: {
            card: cardNumberElement,
            billing_details: {
              address: {
                state: data.usState,
                city: data.city,
                line1: data.address,
                postal_code: data.zipCode
              }
            }
          } }
        );

        if (error) {
          alert(error.message);
        } else {
          navigate(routeDirectory.authentication.onboardingComplete.path());
        }
      }
      setLoading(false);
    }
  }



  return (
    <form onSubmit={form.handleSubmit(processSubmit, e => console.log(e))}>
      <Grid container spacing={3}>
        <Grid item xs={6}>
          <ControlledStripeElementField
            formControl={form.control}
            label="Card Number"
            fieldKey="cardNumber"/>
        </Grid>

        <Grid item xs={3}>
          <ControlledStripeElementField
            formControl={form.control}
            label="Exp. Date"
            fieldKey="expirationDate"/>
        </Grid>

        <Grid item xs={3}>
          <ControlledStripeElementField
            formControl={form.control}
            label="CVV"
            fieldKey="cvv"/>
        </Grid>

        <Grid
          item
          xs={4}>
          <PaymentCountrySelectFieldController
            fieldKey="country"
            formControl={form.control}
          />
        </Grid>

        <Grid
          item
          xs={4}>
          <USStateSelectFieldController
            formControl={form.control}
            fieldKey="usState" />
        </Grid>

        <Grid
          item
          xs={4}>
          <ControlledTextField
            formControl={form.control}
            label="City"
            fieldKey="city" />
        </Grid>

        <Grid
          item
          xs={9}>
          <ControlledTextField
            formControl={form.control}
            label="Address"
            fieldKey="address" />
        </Grid>

        <Grid item xs={3}>
          <ControlledTextField
            formControl={form.control}
            label="Zip Code"
            fieldKey="zipCode" />
        </Grid>

        <Grid item xs={12}>
          <Divider />
        </Grid>

        <Grid item xs={5}>
          <ControlledTextField
            formControl={form.control}
            label="Discount Code"
            fieldKey="discountCode" />
        </Grid>

        <Grid item xs={12}>
          <Stack direction='row' justifyContent='space-between'>
            <Button
              disabled={loading}
              variant="outlined"
              color="secondary"
              onClick={() => navigate(-1)}>
              Back
            </Button>

            <Button
              disabled={loading}
              type="submit">
              Confirm
            </Button>
          </Stack>
        </Grid>
      </Grid>
    </form>
  );
};
